import { useIntl } from 'react-intl'
import DataTable, {
	TableBody,
	TableColumn,
	TableHeader,
	TableRow,
} from 'react-md/lib/DataTables'
import { TableContainer } from 'ui/StyledComponents/Tables'

import { Fragment } from 'react'
import styled from 'styled-components'
import { formatCurrency } from 'util/formatStrings'
import { billingBasisMessage } from '../../utils/messages'
import { useDispatch } from 'react-redux'
import { push } from 'connected-react-router'
import { TableRowContainer, TableRowsNotFound } from '../components'
import type {
	LoanShareholderCalculation,
	LoanShareholderWithApartment,
} from '@viilu-web/shared'

const ShareGroupLink = styled.button`
	background: none;
	border: none;
	color: var(--color-primary);
	text-decoration: underline;
	cursor: pointer;
	padding: 0;
	font: inherit;
`

type Props = {
	shareholdersWithApartmentOptions: LoanShareholderWithApartment[]
	sharegroupDebts: LoanShareholderCalculation[]
	selectedShareholders: string[]
	setSelectedShareholders: (uuids: string[]) => void
}

function LoanShareholdersTable(props: Props) {
	const {
		shareholdersWithApartmentOptions,
		sharegroupDebts,
		selectedShareholders,
		setSelectedShareholders,
	} = props
	const intl = useIntl()
	const dispatch = useDispatch()

	const onShareGroupClick = (shareGroupUUID: string) => {
		const searchParams = new URLSearchParams(window.location.search)

		searchParams.set('sguuid', shareGroupUUID)
		const newUrl = `${window.location.pathname}?${searchParams.toString()}`

		dispatch(push(newUrl))
	}

	const onAllRowsChecked = (checked: boolean) => {
		if (checked) {
			const newSelectedShareholders = shareholdersWithApartmentOptions.map(
				(sg) => sg.uuid,
			)
			setSelectedShareholders(newSelectedShareholders)
		} else {
			setSelectedShareholders([])
		}
	}

	const handleRowSelection = (shareGroupUUID: string) => {
		if (selectedShareholders.includes(shareGroupUUID)) {
			setSelectedShareholders(
				selectedShareholders.filter((uuid) => uuid !== shareGroupUUID),
			)
		} else {
			setSelectedShareholders([...selectedShareholders, shareGroupUUID])
		}
	}

	const renderTableHeaders = () => {
		return (
			<TableRow>
				<TableColumn>
					<p className="text-strong text-subtle">
						{intl.formatMessage({
							defaultMessage: 'Osakeryhmä',
							description: 'Table header for identifier',
						})}
					</p>
				</TableColumn>
				<TableColumn>
					<p className="text-strong text-subtle">
						{intl.formatMessage({
							defaultMessage: 'Alkup. yksiköt',
							description: 'Table header for units',
						})}
					</p>
				</TableColumn>
				<TableColumn>
					<p className="text-strong text-subtle">
						{intl.formatMessage({
							defaultMessage: 'Velalliset yksiköt',
							description: 'Table header for units',
						})}
					</p>
				</TableColumn>
				<TableColumn>
					<p className="text-strong text-subtle">
						{intl.formatMessage({
							defaultMessage: 'Jakoperuste',
							description: 'Table header for billing basis',
						})}
					</p>
				</TableColumn>
				<TableColumn>
					<p className="text-strong text-subtle">
						{intl.formatMessage({
							defaultMessage: 'Lainaosuus',
							description: 'Table header for loan share amount',
						})}
					</p>
				</TableColumn>
				<TableColumn>
					<p className="text-strong text-subtle">
						{intl.formatMessage({
							defaultMessage: 'Velkaosuus',
							description: 'Table header for sharegroup debt amount',
						})}
					</p>
				</TableColumn>
			</TableRow>
		)
	}

	const renderTableRows = () => {
		return shareholdersWithApartmentOptions
			.map((shareholder, index) => {
				const correspondingSharegroupDebt = sharegroupDebts.find(
					(debt) => debt.shareGroupUUID === shareholder.shareGroupUUID,
				)
				return (
					<Fragment key={shareholder.uuid}>
						<TableRowContainer
							$filledBackground={index % 2 === 0}
							onCheckboxClick={() => handleRowSelection(shareholder.uuid)}
							selected={selectedShareholders.includes(shareholder.uuid)}
						>
							<TableColumn>
								{shareholder.apartment ? (
									<ShareGroupLink
										onClick={() =>
											onShareGroupClick(shareholder.shareGroupUUID)
										}
									>
										{shareholder.apartment.label}
									</ShareGroupLink>
								) : (
									<p>{shareholder.shareGroupUUID}</p>
								)}
							</TableColumn>
							<TableColumn>{shareholder.originalUnits}</TableColumn>
							<TableColumn>
								{correspondingSharegroupDebt
									? correspondingSharegroupDebt.units
									: 0}
							</TableColumn>
							<TableColumn>
								{shareholder.billingBasis
									? billingBasisMessage(intl, shareholder.billingBasis)
									: ''}
							</TableColumn>
							<TableColumn>
								{formatCurrency(
									correspondingSharegroupDebt?.baseShareAmount || 0,
								)}
							</TableColumn>
							<TableColumn>
								{correspondingSharegroupDebt
									? formatCurrency(correspondingSharegroupDebt.debtAmount)
									: formatCurrency(shareholder.originalLoanShareAmount)}
							</TableColumn>
						</TableRowContainer>
					</Fragment>
				)
			})
			.concat(
				<tr
					key="empty-row"
					className="full-width"
					style={{ height: 100, background: 'white' }}
				></tr>,
			)
	}

	return (
		<>
			{shareholdersWithApartmentOptions?.length > 0 ? (
				<TableContainer>
					<DataTable
						baseId="loan-shareholders-table"
						selectableRows
						onRowToggle={(rowIdx, checked) => {
							rowIdx === 0 && onAllRowsChecked(checked)
						}}
						className="md-paper--1"
						style={{
							background: 'white',
							maxHeight: 'calc(100vh - 205px)',
						}}
					>
						<TableHeader
							style={{
								position: 'sticky',
								top: 0,
								zIndex: 1,
								background: 'white',
								borderBottom: 'rgba(0, 0, 0, 0.12) solid 2px',
							}}
						>
							{renderTableHeaders()}
						</TableHeader>
						<TableBody>{renderTableRows()}</TableBody>
					</DataTable>
				</TableContainer>
			) : (
				<TableRowsNotFound>
					{intl.formatMessage({
						defaultMessage: 'Osakkaita ei löytynyt',
						description: 'Error text when no loan shareholders were found',
					})}
				</TableRowsNotFound>
			)}
		</>
	)
}

export default LoanShareholdersTable
